import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import Card from "../../../../components/card";
import HttpManager from "../../../../services/HttpManager";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import VentanaVenta from "../../../../components/kits/ventanaVenta";
import useEsTelefono from "../../../../utils/esTelefono";
import { useCampus, useFilterOptions, usePerms } from "../kits/shared";
import MetasBonos from "../sales/metasBonos";
import TabVentas from "./tabVentas";
import TabSinKit from "./tabSinKit";
import TabVendedores from "./tabVendedores";
import TabMargen from "./tabMargen";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import "./ventas.css";

/*
	Tienda › Ventas (Módulos Claros, Fase 1, 2026-11-01). Una sola pantalla para lo que eran Control de Ventas (38 kitSales),
	Nueva Venta (66 newSale), Maestros (68 teacherBoard) y Metas y Bonos (67 salesGoals). Las cuatro rutas abren esta pantalla y
	cada pestaña sale según el permiso de siempre:
	  Ventas      → 38 o 66 (Leer); las 4 cifras y los botones de la ventana, solo con 38.
	  Sin Kit     → 38.
	  Vendedores  → 38 o 68.
	  Bonos       → 67.
	  Margen      → 63 (Márgenes sigue en el menú hasta la Fase 2).
	«+ Nueva Venta» (66 Crear) abre la misma ventana que «Cargar Productos» del Estado de Cuenta. ?nueva=1 la abre al entrar.
*/

type TabKey = "ventas" | "sinKit" | "vendedores" | "bonos" | "margen";

const Ventas: React.FC = () => {
	const { campusId, currency } = useCampus();
	const movil = useEsTelefono();
	const location = useLocation();
	const navigate = useNavigate();
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const superAdmin = currentUser?.currentRole === "superAdmin";
	const p38: any = usePerms("kitSales");
	const p66: any = usePerms("newSale");
	const p67: any = usePerms("salesGoals");
	const p68: any = usePerms("teacherBoard");
	const p63: any = usePerms("productMargins");
	const lee = (p: any) => superAdmin || !!p?.read;
	const tiene38 = lee(p38);

	const tabs = useMemo(() => {
		const t: { key: TabKey; label: string }[] = [];
		if (tiene38 || lee(p66)) t.push({ key: "ventas", label: "Ventas" });
		if (tiene38) t.push({ key: "sinKit", label: "Sin Kit" });
		if (tiene38 || lee(p68)) t.push({ key: "vendedores", label: "Vendedores" });
		if (lee(p67)) t.push({ key: "bonos", label: "Bonos" });
		if (lee(p63)) t.push({ key: "margen", label: "Margen" });
		return t;
	}, [tiene38, p66?.read, p67?.read, p68?.read, p63?.read, superAdmin]);

	// Quien entra por la ruta vieja de Maestros o Metas cae en su pestaña.
	const inicial: TabKey = location.pathname.startsWith("/teacherBoard") ? "vendedores" : location.pathname.startsWith("/salesGoals") ? "bonos" : "ventas";
	const [tab, setTab] = useState<TabKey>(inicial);
	const activa: TabKey = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key || "ventas");
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [vendiendo, setVendiendo] = useState<boolean>(false);
	const [vuelta, setVuelta] = useState<number>(0);
	const [aviso, setAviso] = useState<string | null>(null);
	const [sinKit, setSinKit] = useState<number | null>(null);
	const puedeVender = superAdmin || !!p66?.create;
	const seccionLista: "kitSales" | "newSale" = tiene38 ? "kitSales" : "newSale";
	const opciones = useFilterOptions(campusId, seccionLista as any);

	useEffect(() => {
		if (new URLSearchParams(location.search).get("nueva") === "1" && puedeVender) {
			setVendiendo(true);
			navigate(location.pathname, { replace: true });
		}
	}, [location.search]);
	useEffect(() => { if (!aviso) return; const t = window.setTimeout(() => setAviso(null), 10000); return () => window.clearTimeout(t); }, [aviso]);
	// Número de la pestaña Sin Kit (horarios con alumnas sin kit) desde que se abre la pantalla.
	useEffect(() => {
		setSinKit(null);
		if (!campusId || !tiene38) return;
		let vigente = true;
		HttpManager.getInstance().salesService.withoutKit(campusId, { onlyMissing: 1 }).then((r) => { if (vigente) setSinKit(r.data.length); }).catch(() => undefined);
		return () => { vigente = false; };
	}, [campusId, tiene38]);

	if (!campusId) return <div style={{ padding: 24 }}>Selecciona un plantel.</div>;

	const cambiar = (k: TabKey) => { setTab(k); setSearch(""); setQ(""); };
	const conBuscador = activa !== "bonos";

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>Ventas</h4>
				{puedeVender ? (
					<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setVendiendo(true)}>
						<FontAwesomeIcon icon={solid("plus")} />&nbsp;Nueva Venta
					</button>
				) : null}
			</div>
			{aviso ? <div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={() => setAviso(null)} /></div> : null}

			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
							{tabs.map((t) => (
								<li className="nav-item" key={t.key}>
									<button type="button" className={"nav-link btn btn-sm " + (activa === t.key ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => cambiar(t.key)}>
										{t.label}{t.key === "sinKit" && sinKit ? <span className="waTabCuenta">{sinKit}</span> : null}
									</button>
								</li>
							))}
						</ul>
						{conBuscador ? (
							<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
								<SearchInputComponent search={search} onChangeSearch={setSearch} onClickSearch={() => setQ(search.trim())} onClearSearch={() => { setSearch(""); setQ(""); }} />
							</div>
						) : null}
					</div>

					{tabs.length < 1 ? <div className="wvtSub" style={{ padding: 24 }}>Sin Acceso</div>
						: activa === "ventas" ? <TabVentas campusId={campusId} sym={currency} movil={movil} q={q} section={seccionLista} opciones={opciones}
							conCifras={tiene38} canAct={superAdmin || !!p38?.create} vuelta={vuelta} onAviso={setAviso} />
						: activa === "sinKit" ? <TabSinKit campusId={campusId} movil={movil} q={q} opciones={opciones} onCuenta={setSinKit} />
						: activa === "vendedores" ? <TabVendedores campusId={campusId} sym={currency} movil={movil} q={q} section={tiene38 ? "kitSales" : "teacherBoard"} />
						: activa === "bonos" ? <MetasBonos embebido />
						: <TabMargen campusId={campusId} sym={currency} movil={movil} q={q} />}
				</div>
			</Card>
			{vendiendo ? <VentanaVenta campusId={campusId} currency={currency} from="newSale" canCharge={puedeVender}
				onClose={() => setVendiendo(false)} onDone={() => setVuelta((n) => n + 1)} /> : null}
		</div>
	);
};

export default Ventas;
